import { Col, Container, Row, Form } from "react-bootstrap";
import { ChangeEvent, forwardRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import "./TextInput.css";


const DEFAULT_MAX_INPUT = 40;

export type TextInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "name" | "value" | "onChange" | "onBlur" | "defaultValue"
> & {
  name: string;
  labelText?: string;
  required?: boolean;
  width?: string;
  value?: string;
  maxLength?: number;
  normalize?: (raw: string) => string;
  onChange?: (next: string, e: ChangeEvent<HTMLInputElement>) => void;
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      labelText,
      required,
      placeholder,
      width,
      value,
      maxLength,
      tabIndex,
      normalize,
      onChange,
      name,
      ...rest
    }: TextInputProps,
    ref
  ) => {
    const inputMaxLength = maxLength || DEFAULT_MAX_INPUT;
    const { control } = useFormContext();

    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value ?? "";
            const next = normalize ? normalize(raw) : raw;
            field.onChange(next);
            onChange?.(next, e);
          };

          return (
            <Container fluid>
              <Row
                className={labelText ? "mt-4" : ""}
                style={{ width: width || "auto" }}
              >
                <Col>
                  <Form.Group>
                    <Row>
                    {labelText && (
                      <Form.Label className="fw-bold fs-6 text-input-label">
                        {labelText}{" "}
                        {required && (
                          <span className="input-required-marker">*</span>
                        )}
                      </Form.Label>
                    )}
                    </Row>
                    <Row>
                        <Form.Control
                        style={{ height: 49 }}
                          {...rest}
                          name={name}
                          ref={field.ref}
                          type="text"
                          className="text-input py-1 px-2 fs-5"
                          maxLength={inputMaxLength}
                          placeholder={placeholder}
                          value={field.value ?? ""}
                          onChange={handleChange}
                          onBlur={field.onBlur}
                          tabIndex={tabIndex}
                        />
                    </Row>
                    <Row>
                        {fieldState.error && (
                          <div className="text-input-error-message ps-0">
                            {fieldState.error.message}
                          </div>
                        )}
                    </Row>
                  </Form.Group>
                </Col>
              </Row>
            </Container>
          );
        }}
      />
    );
  }
);

export default TextInput;
